import { type CSSProperties, type FC, type MouseEvent, useCallback } from "react";

import { cx } from "@emotion/css";

import { type MyLensesLens, TosKey } from "../../../../gql-generated/types";
import {
    IconSection,
    LensContainer,
    LensDestinationsTooltip,
    LensIcon,
    LensStatusWithWarning,
    LensUpdateAtDate,
    StatusSection,
    tableTextStyle,
} from "../../../../lens/components/shared";
import { useLensDestinationTypesFromLens, useSubmissionErrors } from "../../../../lens/hooks";
import { LOCKED_BASE_STATUSES } from "../../../../lens/state";
import {
    VISIBILITY_OPTION_ICON_MAP,
    VISIBILITY_OPTION_LABEL_MAP,
    VisibilityLensRow,
} from "../../../../lens-visibility/components";
import { visibilityWidth } from "../../../../lenses/components/list/header";
import { LensSurfaceTypeTag } from "../../../../lenses/components/list/row";
import { LensCreatorPayoutBadge } from "../../../../lenses/components/list/row/lens-creator-payout-badge";
import { type LensModalProps } from "../../../../lenses/models";
import { Flexbox, TruncateComponent } from "../../../../shared/components";
import { useHoveredState, useTos } from "../../../../shared/hooks";

import { LensLinkWrapper } from "./LensLinkWrapper";
import { iconWidth, lensNameWidth, statusWidth } from "./LensRow.styles";

interface LensRowProps {
    style: CSSProperties;
    lens: MyLensesLens;
    distributionRestrictionsProps: LensModalProps;
}

export const LensRow: FC<LensRowProps> = ({ lens, style, distributionRestrictionsProps }) => {
    const { acceptedLatestVersion } = useTos(TosKey.LensCreatorPayoutTos);
    const destinations = useLensDestinationTypesFromLens(lens);

    const { hasSubmissionErrors } = useSubmissionErrors(lens.id);

    const isLensLocked = LOCKED_BASE_STATUSES.has(lens.status);

    const { isHovered: isStatusHovered, handleMouseEnter, handleMouseLeave } = useHoveredState();

    const onDestinationHeaderClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            distributionRestrictionsProps.setLens(lens);
            distributionRestrictionsProps.showModal();
            event.stopPropagation();
        },
        [distributionRestrictionsProps, lens],
    );

    return (
        <LensContainer style={style} id={lens.id} data-testid="lens-row">
            <LensLinkWrapper lens={lens}>
                <IconSection className={iconWidth}>
                    <LensIcon src={lens.iconUrl} />
                </IconSection>
                <TruncateComponent
                    className={cx(tableTextStyle, lensNameWidth)}
                    content={
                        <>
                            {lens.name}
                            <LensSurfaceTypeTag surfaceType={lens.surfaceType} />
                            <LensCreatorPayoutBadge
                                lensCreatorPayoutEligibility={lens.lensCreatorPayoutEligibility}
                                acceptedLatestTosVersion={acceptedLatestVersion}
                                exclusiveLensStatus={lens.exclusiveLensStatus}
                            />
                        </>
                    }
                />
                <LensUpdateAtDate seconds={lens.updatedAt?.seconds ?? 0} />
                <StatusSection className={statusWidth} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                    <LensStatusWithWarning
                        status={lens.status}
                        hasSubmissionErrors={hasSubmissionErrors}
                        destinations={destinations}
                        showDestinationsText={true}
                        loading={false}
                        hasDistributionRestrictions={
                            distributionRestrictionsProps.isEnabled && lens.distributionRestrictionsList.length > 0
                        }
                    />

                    {destinations.length > 0 && !isLensLocked && isStatusHovered && (
                        <Flexbox justifyContent="center">
                            <LensDestinationsTooltip
                                lens={lens}
                                numDestinations={destinations.length}
                                isDisabledInDiscoverEnabled={distributionRestrictionsProps.isEnabled}
                                onDestinationHeaderClick={onDestinationHeaderClick}
                            />
                        </Flexbox>
                    )}
                </StatusSection>
                <VisibilityLensRow
                    dataTestId="lens-row-visibility"
                    className={visibilityWidth}
                    label={VISIBILITY_OPTION_LABEL_MAP[lens.visibility]}
                    Icon={VISIBILITY_OPTION_ICON_MAP[lens.visibility]}
                />
            </LensLinkWrapper>
        </LensContainer>
    );
};
